// Auth API utility functions
// Handles login, registration, logout, forgot/reset password endpoints
import api from './index';
import TokenManager from '../utils/tokenManager';

// Login user with credentials (email, password)
export const login = async (credentials) => {
  return api.post('/auth/login', credentials);
};

// Register a new user
export const register = (data) => api.post('/auth/register', data);

// Logout user: calls backend to invalidate session and removes local token
export const logout = async () => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      // Revoke refresh token and server session before clearing client state.
      // The server should clear the HttpOnly refresh cookie and invalidate any
      // server-side session or token record.
      try {
        await api.post('/auth/revoke-token', {});
      } catch (e) {
        // If revoke fails, we still continue to clear local client state.
        // Log the error for monitoring and debugging purposes
        console.error('Failed to revoke token during logout:', e);
      }
    }
  } catch (e) {
    // Log any other errors during logout process
    console.error('Error during logout process:', e);
  } finally {
    // Clear all auth-related data
    TokenManager.clearAuthData();
  }
};

// Explicit revoke function (can be used separately when needed)
export const revokeToken = async () => {
  try {
    return await api.post('/auth/revoke-token', {});
  } catch (err) {
    // Log error for monitoring and debugging purposes
    console.error('Failed to revoke token:', err);
    // bubble up error to caller if they want to handle UI
    throw err;
  }
};

// Change password for logged-in user
export const changePassword = async (currentPassword, newPassword, confirmPassword) => {
  // The fetch-based API client automatically includes the Authorization header
  return api.post('/auth/change-password', { currentPassword, newPassword, confirmPassword });
};

// Request password reset email
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });

// Reset password using token, new password, and confirm password
export const resetPassword = (token, newPassword, confirmPassword) =>
  api.post('/auth/reset-password', { token, newPassword, confirmPassword });

// Note: Token refresh is now handled automatically by the API client in src/api/index.js
// using HttpOnly cookies with credentials: 'include'. No manual refresh functions needed.

// Utility function to get stored token
export const getStoredToken = () => localStorage.getItem('token');

// Utility function to set token in localStorage
export const setStoredToken = (token) => {
  if (token) localStorage.setItem('token', token);
};

// Utility function to clear stored token
export const clearStoredToken = () => {
  localStorage.removeItem('token');
};
